import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, ShoppingBag } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import UserNavbar from "../../components/UserNavbar";

interface CatalogRow {
  _id?: string;
  id?: string;
  name?: string;
  description?: string;
  image?: string;
  image_url?: string;
  subcategories?: CatalogRow[];
  subCategories?: CatalogRow[];
  sub_categories?: CatalogRow[];
}

const HOME_CATEGORIES_API = "https://www.frendrops.in/api/home/categories";

const rowId = (row?: CatalogRow | null) => String(row?._id || row?.id || "");
const rowImage = (row?: CatalogRow | null) => row?.image || row?.image_url || "";
const childRows = (row?: CatalogRow | null) =>
  row?.subcategories || row?.subCategories || row?.sub_categories || [];

const normalizeCategories = (payload: any): CatalogRow[] => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data.filter(Boolean);
  if (!data || typeof data !== "object") return [];
  const list =
    data.categories ||
    data.mainCategories ||
    data.main_categories ||
    data.app_categories ||
    [];
  return Array.isArray(list) ? list.filter(Boolean) : [];
};

export default function ProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState<CatalogRow[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const selectedCategory = useMemo(
    () => categories.find((item) => rowId(item) === selectedId) || categories[0] || null,
    [categories, selectedId]
  );

  const subCategories = childRows(selectedCategory);

  const loadCatalog = useCallback(async (categoryId?: string) => {
    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(HOME_CATEGORIES_API, {
        params: categoryId ? { categoryId } : undefined,
      });
      const list = normalizeCategories(data);
      setCategories(list);
      const requested = categoryId && list.find((item) => rowId(item) === categoryId);
      const next = requested || list[0];
      setSelectedId(rowId(next));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load categories");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const preservedCategoryId =
      (location.state as { categoryId?: string } | null)?.categoryId ||
      sessionStorage.getItem("customerSelectedCategoryId") ||
      "";
    loadCatalog(preservedCategoryId || undefined);
  }, [loadCatalog, location.state]);

  const selectCategory = async (category: CatalogRow) => {
    const id = rowId(category);
    if (!id || id === selectedId) return;
    setSelectedId(id);
    sessionStorage.setItem("customerSelectedCategoryId", id);
    if (childRows(category).length === 0) await loadCatalog(id);
  };

  const openSubCategory = (item: CatalogRow) => {
    const id = rowId(item);
    if (!id) return;
    const categoryId = rowId(selectedCategory);
    if (categoryId) sessionStorage.setItem("customerSelectedCategoryId", categoryId);
    navigate(`/productList?subcategoryId=${encodeURIComponent(id)}&subcategory=${encodeURIComponent(item.name || "")}`, {
      state: { categoryId },
    });
  };

  return (
    <div className="min-h-screen bg-[#f8f7ff] pb-20 md:pb-8">
      <UserNavbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-40">

        {loading ? (
          <div className="min-h-[45vh] flex flex-col items-center justify-center text-gray-500">
            <RefreshCw size={34} className="animate-spin text-violet-700" />
            <p className="mt-4">Loading products...</p>
          </div>
        ) : error ? (
          <div className="min-h-[45vh] flex flex-col items-center justify-center text-center">
            <p className="text-red-600">{error}</p>
            <button onClick={() => loadCatalog()} className="mt-4 bg-violet-700 text-white px-5 py-3 rounded-xl font-semibold">
              Try Again
            </button>
          </div>
        ) : (
          <>
            <section className="mt-6">
              <div className="flex gap-3 overflow-x-auto pb-3">
                {categories.map((category) => {
                  const id = rowId(category);
                  const active = id === rowId(selectedCategory);
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => selectCategory(category)}
                      className={`shrink-0 min-w-[92px] rounded-2xl p-2.5 border transition ${
                        active
                          ? "bg-violet-700 border-violet-700 text-white shadow-md"
                          : "bg-white border-gray-200 text-gray-700"
                      }`}
                    >
                      {rowImage(category) ? (
                        <img src={rowImage(category)} alt={category.name || "Category"} className="w-14 h-14 rounded-xl object-cover mx-auto bg-white" />
                      ) : (
                        <div className={`w-14 h-14 rounded-xl mx-auto flex items-center justify-center ${active ? "bg-white/15" : "bg-violet-50"}`}>
                          <ShoppingBag size={24} className={active ? "text-white" : "text-violet-700"} />
                        </div>
                      )}
                      <span className="block mt-2 text-xs font-bold max-w-[100px] truncate">{category.name || "Category"}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="mt-4">
              <h2 className="text-2xl font-extrabold text-gray-900">{selectedCategory?.name || "Categories"}</h2>
              <p className="text-gray-500 mt-1">{selectedCategory?.description || "Choose a sub category"}</p>

              {subCategories.length === 0 ? (
                <div className="min-h-[35vh] flex items-center justify-center text-gray-500 font-semibold">
                  No sub categories found
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
                  {subCategories.map((item) => (
                    <button
                      type="button"
                      key={rowId(item)}
                      onClick={() => openSubCategory(item)}
                      className="bg-white rounded-2xl overflow-hidden text-left border border-gray-100 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition"
                    >
                      {rowImage(item) ? (
                        <img src={rowImage(item)} alt={item.name || "Sub category"} className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="w-full aspect-square bg-violet-50 flex items-center justify-center">
                          <ShoppingBag size={38} className="text-violet-300" />
                        </div>
                      )}
                      <div className="p-3">
                        <h3 className="font-bold text-gray-900">{item.name || "Sub category"}</h3>
                        {item.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
